import React, { useEffect, useState } from "react";
import { Col, Container, Form, Row, Table } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { useLoaderData } from "react-router";
import { Link } from "react-router-dom";
import Select from "react-select";

import { ITeamRating } from "../Clubhouse/components/Ratings/types/ratings";

import { store } from "../..";
import {
    globalActions,
    selectSetting,
} from "../../features/global/globalSlice";
import {
    leagueManagementActions,
    selectYears,
} from "../../features/leaguemgmt/leagueManagementSlice";
import { useAppSelector } from "../../hooks/reduxHooks";
import { getAllTeamRatings } from "../../lib/api";
import { TEAM_LOGO_URL } from "../../lib/globals";
import { ITeam } from "../../types/api";

interface Filters {
    team: string | undefined;
    coach: string | undefined;
    headCoach: number | undefined;
    asstCoach: number | undefined;
    spectators: number | undefined;
    overall: number | undefined;
    ageLevels: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    year: {
        options: Array<number>;
        filter: number | undefined;
    };
}

interface CSVData {
    team: string;
    ageLevel: string;
    coach: string;
    headCoach: string;
    asstCoach: string;
    spectators: string;
    overall: string;
    count: string;
}

export const loader = async () => {
    const settings = store.getState().global.settings;
    const { years } = store.getState().leaguemanagement;

    const promises = [];

    !years && promises.push(store.dispatch(leagueManagementActions.getYears()));
    !settings.length &&
        promises.push(store.dispatch(globalActions.getSettings()));

    const [{ ratings }] = await Promise.all([getAllTeamRatings(), ...promises]);

    return {
        ratings,
    };
};

const CoachRatings = (): React.JSX.Element => {
    const { ratings: loadedRatings } = useLoaderData() as {
        ratings: ITeamRating[];
    };

    const [ratings, setRatings] = useState<ITeamRating[]>(loadedRatings);
    const [teams, setTeams] = useState<
        Array<
            ITeamRating["team"] & {
                currentAgeLevel: ITeam["ageLevels"][number] | null;
            }
        >
    >([]);
    const years = useAppSelector(selectYears);

    const currentYear = useAppSelector(state =>
        selectSetting(state, "majorybl_current_league_year"),
    );

    const [csvData, setCSVData] = useState<Array<CSVData>>([]);

    const ratingOptions = [
        {
            value: 5,
            label: 5,
        },
        {
            value: 4,
            label: 4,
        },
        {
            value: 3,
            label: 3,
        },
        {
            value: 2,
            label: 2,
        },
        {
            value: 1,
            label: 1,
        },
    ];

    const calculateRating = (
        type: keyof ITeamRating["ratings"],
        ratings: ITeamRating[],
    ) => {
        const total = ratings.reduce(
            (acc, r) => acc + (r.ratings[type] || 0),
            0,
        );

        const count = ratings.reduce(
            (acc, r) =>
                acc +
                (r.ratings[type] !== undefined && r.ratings[type] !== null
                    ? 1
                    : 0),
            0,
        );

        return count > 0 ? (total / count).toFixed(3) : "N/A";
    };

    const handleYearChange = async (year: number | undefined) => {
        const { ratings } = await getAllTeamRatings(year);
        setRatings(ratings);
    };

    useEffect(() => {
        const preloadTeams = Array.from(
            new Set(ratings.map(i => parseInt(i.team.id.toString()))),
        ).map(
            t => ratings.find(r => parseInt(r.team.id.toString()) === t)!.team,
        );

        const teams = preloadTeams.map(team => {
            const ageLevel = team.ageLevels.find(a => {
                return filters.year.filter
                    ? a.year === filters.year.filter
                    : a.year === currentYear?.setting;
            });

            return {
                ...team,
                currentAgeLevel: ageLevel || null,
            };
        });

        teams.sort((a, b) => {
            return (
                (a.currentAgeLevel
                    ? parseInt(a.currentAgeLevel?.ageLevel)
                    : 0) -
                (b.currentAgeLevel ? parseInt(b.currentAgeLevel?.ageLevel) : 0)
            );
        });

        setTeams(teams);
    }, [ratings]);

    const [filters, setFilters] = useState<Filters>({
        team: undefined,
        coach: undefined,
        headCoach: undefined,
        asstCoach: undefined,
        spectators: undefined,
        overall: undefined,
        ageLevels: {
            options: Array.from(
                new Set(teams.flatMap(t => t.ageLevels.map(a => a.ageLevel))),
            ).sort((a, b) => parseInt(a) - parseInt(b)),
            filter: undefined,
        },
        year: {
            options: years
                ? Array.from(Object.keys(years)).map(y => parseInt(y))
                : [],
            filter: parseInt(currentYear?.setting) || undefined,
        },
    });

    const checkRatingsFilters = (ratings: {
        headCoach: string;
        asstCoach: string;
        spectators: string;
        overall: string;
    }): boolean => {
        let returnValue = true;
        for (let key of Object.keys(ratings) as (keyof typeof ratings)[]) {
            if (filters[key]) {
                // Check if the rating is "N/A" or not a number
                if (ratings[key] === "N/A") {
                    continue;
                }

                // Check if the rating is below the filter
                if (parseFloat(ratings[key]) > filters[key]!) {
                    returnValue = false;
                    break; // Stop checking if any rating is below the filter
                }
            }
        }

        return returnValue;
    };

    const CSVHeaders = [
        { label: "Team", key: "team" },
        { label: "Age Level", key: "ageLevel" },
        { label: "Coach/Contact", key: "coach" },
        { label: "Head Coach Rating", key: "headCoach" },
        { label: "Asst Coach Rating", key: "asstCoach" },
        { label: "Spectators Rating", key: "spectators" },
        { label: "Overall Rating", key: "overall" },
        { label: "Count", key: "count" },
    ];

    const handleDownload = () => {
        const filteredData = teams
            .filter(t =>
                filters.team
                    ? t.name.toLowerCase().includes(filters.team.toLowerCase())
                    : true,
            )
            .filter(t =>
                filters.coach
                    ? t.coach
                          .toLowerCase()
                          .includes(filters.coach.toLowerCase())
                    : true,
            )
            .filter(t =>
                filters.ageLevels.filter && t.currentAgeLevel
                    ? filters.ageLevels.filter.includes(
                          t.currentAgeLevel?.ageLevel,
                      )
                    : true,
            )
            .map(t => {
                const teamRatings = ratings.filter(
                    r =>
                        parseInt(r.team.id.toString()) ===
                        parseInt(t.id.toString()),
                );

                const ratingsTotals = {
                    headCoach: calculateRating("headCoach", teamRatings),
                    asstCoach: calculateRating("asstCoach", teamRatings),
                    spectators: calculateRating("spectators", teamRatings),
                    overall: calculateRating("overall", teamRatings),
                };

                return checkRatingsFilters(ratingsTotals)
                    ? {
                          team: t.name,
                          ageLevel: t.currentAgeLevel?.ageLevel,
                          coach: t.coach,
                          headCoach: ratingsTotals.headCoach,
                          asstCoach: ratingsTotals.asstCoach,
                          spectators: ratingsTotals.spectators,
                          overall: ratingsTotals.overall,
                          count: teamRatings.length.toString(),
                      }
                    : null;
            })
            .filter((data): data is CSVData => data !== null);

        setCSVData(filteredData);
    };

    return (
        <div data-testid="ratingsContent" className="ratingsContent">
            <Container>
                <Row className="ratingsFilters">
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Team:</h5>
                        <Form.Control
                            type="text"
                            placeholder="Team"
                            onChange={e => {
                                setFilters(prev => ({
                                    ...prev,
                                    team:
                                        e.target.value.trim() === ""
                                            ? undefined
                                            : e.target.value.trim(),
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Coach:</h5>
                        <Form.Control
                            type="text"
                            placeholder="Coach/Contact"
                            onChange={e => {
                                setFilters(prev => ({
                                    ...prev,
                                    coach:
                                        e.target.value.trim() === ""
                                            ? undefined
                                            : e.target.value.trim(),
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Age Level:</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={true}
                            options={filters.ageLevels.options.map((a, idx) => {
                                return {
                                    value: idx,
                                    label: a,
                                };
                            })}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    ageLevels: {
                                        ...prev.ageLevels,
                                        filter:
                                            a.length === 0
                                                ? undefined
                                                : a?.map(o => o.label),
                                    },
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Year:</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={filters.year.options.map(y => {
                                return {
                                    value: y,
                                    label: y,
                                };
                            })}
                            defaultValue={
                                filters.year.filter
                                    ? {
                                          value: filters.year.filter,
                                          label: filters.year.filter,
                                      }
                                    : undefined
                            }
                            onChange={y => {
                                setFilters(prev => ({
                                    ...prev,
                                    year: {
                                        ...prev.year,
                                        filter: y?.value,
                                    },
                                }));
                                handleYearChange(y?.value);
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Head Coach</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={ratingOptions}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    headCoach: a?.value,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Asst. Coaching</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={ratingOptions}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    asstCoach: a?.value,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Spectators</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={ratingOptions}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    spectators: a?.value,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={3} className="my-2">
                        <h5>Overall</h5>
                        <Select
                            isSearchable={false}
                            isClearable={true}
                            isMulti={false}
                            options={ratingOptions}
                            onChange={a => {
                                setFilters(prev => ({
                                    ...prev,
                                    overall: a?.value,
                                }));
                            }}
                        />
                    </Col>
                    <Col xs={12}>
                        <p style={{ marginBottom: 0, marginTop: ".5em" }}>
                            Ratings filters will filter by max rating value (ie.
                            3 will filter any ratings up to a max of 3)
                        </p>
                    </Col>
                </Row>

                <Row>
                    <Col className="d-flex justify-content-end">
                        <CSVLink
                            data={csvData}
                            headers={CSVHeaders}
                            filename={`coach-ratings.${new Date().getTime()}.csv`}
                            className="btn btn-primary pull-right"
                            target="_blank"
                            onClick={handleDownload}
                        >
                            Export Ratings Data
                        </CSVLink>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Table
                            className="currentYearRatings team"
                            striped
                            responsive
                        >
                            <thead>
                                <tr>
                                    <th>Team Page</th>
                                    <th>Team</th>
                                    <th>Age</th>
                                    <th>Coach</th>
                                    <th>Head Coach</th>
                                    <th>Asst. Coaching</th>
                                    <th>Spectators</th>
                                    <th>Overall</th>
                                    <th>Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teams
                                    .filter(t =>
                                        filters.team
                                            ? t.name
                                                  .toLowerCase()
                                                  .includes(
                                                      filters.team.toLowerCase(),
                                                  )
                                            : true,
                                    )
                                    .filter(t =>
                                        filters.coach
                                            ? t.coach
                                                  .toLowerCase()
                                                  .includes(
                                                      filters.coach.toLowerCase(),
                                                  )
                                            : true,
                                    )
                                    .filter(t =>
                                        filters.ageLevels.filter &&
                                        t.currentAgeLevel
                                            ? filters.ageLevels.filter.includes(
                                                  t.currentAgeLevel?.ageLevel,
                                              )
                                            : true,
                                    )
                                    .map(t => {
                                        const teamRatings = ratings.filter(
                                            r =>
                                                parseInt(
                                                    r.team.id.toString(),
                                                ) === parseInt(t.id.toString()),
                                        );

                                        const ratingsTotals = {
                                            headCoach: calculateRating(
                                                "headCoach",
                                                teamRatings,
                                            ),
                                            asstCoach: calculateRating(
                                                "asstCoach",
                                                teamRatings,
                                            ),
                                            spectators: calculateRating(
                                                "spectators",
                                                teamRatings,
                                            ),
                                            overall: calculateRating(
                                                "overall",
                                                teamRatings,
                                            ),
                                        };

                                        return checkRatingsFilters(
                                            ratingsTotals,
                                        ) ? (
                                            <tr key={t.id}>
                                                <td>
                                                    <Link
                                                        to={`/coachs-clubhouse/team/${t.id}/ratings`}
                                                        target="_blank"
                                                    >
                                                        {t.logo &&
                                                        t.logo !== "" ? (
                                                            <img
                                                                className="teamLogo"
                                                                src={`${TEAM_LOGO_URL}${t.logo}`}
                                                            />
                                                        ) : (
                                                            t.name
                                                        )}
                                                    </Link>
                                                </td>
                                                <td>
                                                    <Link
                                                        to={`/coachs-clubhouse/team/${t.id}/ratings`}
                                                        target="_blank"
                                                    >
                                                        {t.name}
                                                    </Link>
                                                </td>
                                                <td>
                                                    {
                                                        t.currentAgeLevel
                                                            ?.ageLevel
                                                    }
                                                </td>
                                                <td>{t.coach}</td>
                                                <td>
                                                    {ratingsTotals.headCoach}
                                                </td>
                                                <td>
                                                    {ratingsTotals.asstCoach}
                                                </td>
                                                <td>
                                                    {ratingsTotals.spectators}
                                                </td>
                                                <td>{ratingsTotals.overall}</td>
                                                <td>{teamRatings.length}</td>
                                            </tr>
                                        ) : null;
                                    })}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default CoachRatings;
